<template>
  <form-wrapper
    full-width
    full-footer
  >
    <template v-slot:body>
      <validation-observer
        ref="validation"
        tag="div"
        class="col-12 col-lg-8 m-auto"
      >
        <paper
          title="Lifecycle rule configuration"
          body-class="px-2"
        >
          <validation-provider
            #default="{ errors }"
            name="name"
            rules="required"
          >
            <input-field
              label="Name*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                id="name"
                v-model="settings.name"
                :disabled="details"
                placeholder="Enter lifecycle name here..."
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>
          <input-field
            name="Rule scope"
            label="Choose a rule scope"
            label-position="top"
          >
            <b-form-radio-group
              v-model="settings.ruleScope"
              :options="ruleScopeOptions"
              stacked
            />
          </input-field>
          <div class="mt-2 mb-2">
            <div
              v-if="settings.ruleScope === 0"
              key="limit"
            >
              <span class="text-md text-thick">Filter type</span>

              <b-form-radio-group
                v-model="settings.selectedFilter"
                name="radio-sub-component"
                class="mb-1 mt-1 d-flex"
                @change="checkFilter(settings.selectedFilter)"
              >
                <b-form-radio value="prefix">
                  Prefix
                </b-form-radio>
                <b-form-radio value="tag">
                  Tag
                </b-form-radio>
              </b-form-radio-group>
              <div
                v-if="settings.selectedFilter === 'tag'"
                key="tags"
                class="top-fade-in"
              >
                <input-field
                  name="tags"
                  label="Object Tags"
                  label-position="top"
                >
                  <div
                    class="d-flex w-100 align-items-center position-relative"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Key"
                      rules="required"
                      class="w-50 mr-1"
                      tag="div"
                    >
                      <b-input
                        v-model="settings.tag.key"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Key"
                        class="mb-0"
                      />
                    </validation-provider>
                    <b-input
                      v-model="settings.tag.value"
                      placeholder="Value"
                      class="w-50"
                    />
                  </div>
                </input-field>
              </div>
              <div
                v-else
                key="prefix"
                class="top-fade-in"
              >
                <validation-provider
                  #default="{ errors }"
                  name="prefix"
                  tag="div"
                  rules="required"
                >
                  <input-field
                    name="prefix"
                    label="Prefix"
                    label-position="top"
                    :error="errors.length ? errors[0] : null"
                  >
                    <b-form-input
                      id="prefix"
                      v-model="settings.prefix"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Enter prefix here..."
                    />
                  </input-field>
                </validation-provider>
              </div>
            </div>
            <div
              v-else
              key="all"
            >
              <custom-alert
                items-center
                icon-name="InfoAlt"
              >
                If you want the rule to apply to specific objects, you must use
                a filter to identify those objects. Choose "Limit the scope of
                this rule using one or more filters".
              </custom-alert>
            </div>
          </div>
        </paper>
        <paper
          title="Lifecycle rule actions"
          body-class="px-2 pb-2"
        >
          <validation-provider
            #default="{ errors }"
            name="rule action"
            rules="required"
          >
            <b-form-checkbox-group
              v-model="settings.ruleAction"
              :options="ruleActionOptions"
              class="rule-actions"
              @change="checkRule(settings.ruleAction)"
            />
            <span
              v-if="errors.length"
              class="text-danger"
            >*Required</span>
          </validation-provider>
        </paper>
        <paper
          v-if="settings.ruleAction.includes(0)"
          title="Transition current versions of objects between storage classes"
          body-class="px-2 pb-2"
        >
          <lifecycle-tags
            :tags="settings.currentTransitionTags"
            :options="transitionOptions"
          />
        </paper>
        <paper
          v-if="settings.ruleAction.includes(1)"
          title="Transition noncurrent versions of objects between storage classes"
          body-class="px-2 pb-2"
        >
          <lifecycle-tags
            :tags="settings.noncurrentTransitionTags"
            :options="transitionOptions"
            newer-versions
          />
        </paper>
        <paper
          v-if="settings.ruleAction.includes(2)"
          title="Expire current versions of objects"
          body-class="px-2 pb-2"
        >
          <validation-provider
            #default="{ errors }"
            name="Expiration"
            rules="required|minValueRule:3"
            vid="Expiration1"
          >
            <input-field
              label="Days after object creation*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                id="expiration1"
                v-model="settings.expirationDaysCurrentVersion"
                type="number"
                placeholder="Enter number of days..."
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>
        </paper>
        <paper
          v-if="settings.ruleAction.includes(3)"
          title="Permanently delete noncurrent versions of objects"
          body-class="px-2 pb-2 d-flex w-100"
        >
          <validation-provider
            #default="{ errors }"
            name="Expiration"
            vid="Expiration2"
            rules="required|minValueRule:3"
            tag="div"
            class="flex-fill"
          >
            <input-field
              label="Days after objects become noncurrent*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                id="expiration2"
                v-model="settings.expirationDaysNoncurrentVersion"
                type="number"
                placeholder="Enter number of days..."
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>
          <input-field
            label="Number of newer versions to retain"
            label-position="top"
            class="flex-fill ml-1"
          >
            <b-form-input
              id="versions"
              v-model="settings.verisonsNoncurrentVersion"
              type="number"
              placeholder="Enter number of versions..."
            />
          </input-field>
        </paper>
        <paper
          v-if="settings.ruleAction.includes(4)"
          title="Delete expired object delete markers or incomplete multipart uploads"
          body-class="px-2 pb-2"
        >
          <validation-observer
            ref="validate"
            v-slot="{ errors }"
          >
            <validation-provider
              name="expiredObjectDeleteMarker"
              :rules="
                !settings.deleteIncompleteMultipartUploads ? 'required' : ''
              "
              tag="div"
              class="mb-2"
            >
              <span
                class="text-md text-thick"
              >Expired object delete markers</span>
              <b-form-checkbox
                id="checkbox-1"
                v-model="settings.expiredObjectDeleteMarker"
                name="checkbox-1"
                :disabled="settings.ruleAction.includes(2)"
                class="mt-1"
                :value="true"
                :unchecked-value="null"
              >
                Delete expired object delete markers
              </b-form-checkbox>
              <div
                v-if="settings.ruleAction.includes(2)"
                class="mt-1"
              >
                <custom-alert
                  items-center
                  icon-name="InfoAlt"
                >
                  You cannot enable "Delete expired object delete markers" if
                  you enable "Expire current versions of objects".
                </custom-alert>
              </div>
            </validation-provider>

            <validation-provider
              name="deleteIncompleteMultipartUploads"
              :rules="!settings.expiredObjectDeleteMarker ? 'required' : ''"
              tag="div"
              class="mb-1"
            >
              <span
                class="text-md text-thick"
              >Incomplete multipart uploads</span>
              <b-form-checkbox
                id="checkbox-2"
                v-model="settings.deleteIncompleteMultipartUploads"
                name="checkbox-2"
                class="mt-1"
                :value="true"
                :unchecked-value="null"
              >
                Delete incomplete multipart uploads
              </b-form-checkbox>
            </validation-provider>

            <span
              v-if="
                (errors.expiredObjectDeleteMarker &&
                  errors.expiredObjectDeleteMarker.length > 0) ||
                  (errors.deleteIncompleteMultipartUploads &&
                    errors.deleteIncompleteMultipartUploads.length > 0)
              "
              class="text-danger"
            >*At least one action is required for this rule</span>
          </validation-observer>

          <validation-provider
            v-if="settings.deleteIncompleteMultipartUploads"
            #default="{ errors }"
            name="Number of days"
            vid="expiration3"
            rules="required|minValueRule:1"
            tag="div"
            class="flex-fill"
          >
            <input-field
              label="Number of days*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                v-model="settings.daysAfterInitiation"
                type="number"
                placeholder="Enter number of days..."
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>
        </paper>
      </validation-observer>
    </template>
    <template v-slot:footer>
      <b-button
        variant="bg-white"
        @click="$emit('close')"
      >
        Cancel
      </b-button>
      <button-loading
        variant="bg-primary"
        class="ml-16"
        :disabled="addingLifecycle || updatingifecycle"
        :spinning="addingLifecycle || updatingifecycle"
        @click="validateLifecycle"
      >
        {{ details ? 'Update' : 'Create' }}
      </button-loading>
    </template>
  </form-wrapper>
</template>
<script>
import LifecycleTags from '@/components/tags/LifecycleTags.vue'
import Paper from '@/components/paper/Paper.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required, minValueRule } from '@validations'

export default {
  name: 'LifecycleForm',
  components: {
    ValidationObserver,
    ValidationProvider,
    LifecycleTags,
    Paper,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    editedSettings: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      minValueRule,
      settings: {
        name: '',
        tag: {
          key: '',
          value: '',
        },
        prefix: '',
        ruleScope: 0,
        ruleAction: [],
        currentTransitionTags: [
          {
            index: 0,
            rule: 'COLD',
            days: null,
          },
        ],
        noncurrentTransitionTags: [
          {
            index: 0,
            rule: 'COLD',
            days: null,
            versions: null,
          },
        ],
        expirationDaysCurrentVersion: null,
        expirationDaysNoncurrentVersion: null,
        verisonsNoncurrentVersion: null,
        expiredObjectDeleteMarker: null,
        deleteIncompleteMultipartUploads: null,
        daysAfterInitiation: null,
        selectedFilter: 'prefix',
      },
      ruleScopeOptions: [
        {
          text: 'Limit the scope of this rule using one or more filters',
          value: 0,
        },
        { text: 'Apply to all objects in the bucket', value: 1 },
      ],
      transitionOptions: [
        { text: 'Standard', value: 'STANDARD', selectable: false },
        { text: 'Cold', value: 'COLD', selectable: true },
      ],
    }
  },
  computed: {
    addingLifecycle() {
      return this.$store.state.accountLifecycle.addingLifecycle
    },
    updatingifecycle() {
      return this.$store.state.accountLifecycle.updatingifecycle
    },
    ruleActionOptions() {
      return [
        {
          text: 'Move current versions of objects between storage classes',
          value: 0,
        },
        {
          text: 'Move noncurrent versions of objects between storage classes',
          value: 1,
        },
        {
          text: 'Expire current versions of objects',
          value: 2,
        },
        {
          text: 'Permanently delete noncurrent versions of objects',
          value: 3,
        },
        {
          text:
            'Delete expired object delete markers or incomplete multipart uploads',
          value: 4,
          disabled: this.settings.selectedFilter === 'tag',
        },
      ]
    },
  },
  created() {
    if (this.details) {
      this.settings = this.editedSettings
      if (this.settings.selectedFilter === 'prefix') {
        this.settings.tag = {
          key: '',
          value: '',
        }
      }
    }
  },
  methods: {
    validateLifecycle() {
      return new Promise((resolve, reject) => {
        this.$refs.validation.validate().then(success => {
          if (success) {
            resolve()
            this.confirm()
          } else {
            reject()
          }
        })
      })
    },
    confirm() {
      const lifecycle = {
        id: this.settings.name,
      }

      if (this.settings.ruleScope === 0) {
        if (this.settings.selectedFilter === 'tag') {
          lifecycle.lifecycleTagPredicate = {
            tag: this.settings.tag,
          }
        } else {
          lifecycle.lifecyclePrefixPredicate = {
            prefix: this.settings.prefix,
          }
        }
      }

      if (this.settings.expiredObjectDeleteMarker) {
        lifecycle.expiration = {
          expiredObjectDeleteMarker: true,
        }
      }

      if (this.settings.ruleAction.includes(0)) {
        lifecycle.transitions = []
        this.settings.currentTransitionTags.forEach(element => {
          lifecycle.transitions.push({
            days: element.days,
            storageClass: {
              value: element.rule,
            },
          })
        })
      }

      if (this.settings.ruleAction.includes(1)) {
        lifecycle.noncurrentVersionTransitions = []
        this.settings.noncurrentTransitionTags.forEach(element => {
          lifecycle.noncurrentVersionTransitions.push({
            newerNoncurrentVersions: Number(element.versions),
            noncurrentDays: Number(element.days),
            storageClass: {
              value: element.rule,
            },
          })
        })
      }

      if (this.settings.ruleAction.includes(2)) {
        lifecycle.expiration = {
          days: this.settings.expirationDaysCurrentVersion,
        }
      }

      if (this.settings.ruleAction.includes(3)) {
        lifecycle.noncurrentVersionExpiration = {
          noncurrentDays: Number(this.settings.expirationDaysNoncurrentVersion),
          newerNoncurrentVersions: Number(
            this.settings.verisonsNoncurrentVersion,
          ),
        }
      }

      if (
        this.settings.ruleAction.includes(4)
        && this.settings.deleteIncompleteMultipartUploads
        && this.settings.daysAfterInitiation
      ) {
        lifecycle.abortIncompleteMultipartUpload = {
          daysAfterInitiation: Number(this.settings.daysAfterInitiation),
        }
        if (lifecycle.expiration) {
          lifecycle.expiration.expiredObjectDeleteMarker = this.settings.expiredObjectDeleteMarker || false
        } else {
          lifecycle.expiration = {
            expiredObjectDeleteMarker:
              this.settings.expiredObjectDeleteMarker || false,
          }
        }
      }
      this.$emit('save', lifecycle)
    },

    checkFilter(val) {
      if (val === 'tag' && this.settings.ruleAction.includes(4)) {
        this.settings.ruleAction = this.settings.ruleAction.filter(e => e !== 4)
      }
    },
    checkRule(val) {
      if (val.includes(2) && this.settings.expiredObjectDeleteMarker) {
        this.settings.expiredObjectDeleteMarker = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.rule-actions::v-deep {
  .custom-checkbox.custom-control {
    margin-bottom: 5px;
    .custom-control-label {
      &::before {
        top: 2px;
      }
      &::after {
        top: 2px;
      }
    }
  }
}
</style>
